import ReportIssueBar from '@/components/ReportIssueBar';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Upload, FileText, AlertTriangle, Sparkles, CheckCircle, XCircle, X } from "lucide-react";
import axios from "axios";
import toast from 'react-hot-toast';
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';
import LedProgressLine from "@/components/LedProgressLine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

// Remove old token function - now using global Firebase auth via axios interceptor

const ContractReviewScanner = ({ onBack }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("File size must be under 10MB. Please select a smaller file.");
        return;
      }
      setFile(selectedFile);
      setScanResults(null);
    }
  };

  const handleScan = async () => {
    console.log('handleScan called, file:', file);
    
    if (!file) {
      console.log('No file selected');
      toast.error("Please select a contract document to scan.");
      return;
    }

    console.log('Starting scan, API URL:', `${API}/contracts/scan`);
    setScanning(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      console.log('Sending request...');
      const response = await axios.post(
        `${API}/contracts/scan`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('Scan response received:', response.data);
      setScanResults(response.data);
      toast.success("Contract scanned successfully!");
    } catch (error) {
      console.error('Scan error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to scan contract. Please try again.';
      toast.error(errorMessage);
    } finally {
      console.log('Scan complete, setting scanning to false');
      setScanning(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setScanResults(null);
    if (document.getElementById('file-input')) {
      document.getElementById('file-input').value = '';
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'high': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-400';
    }
  };

  const getRiskIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'high': return <XCircle className="text-red-500" size={24} />;
      case 'medium': return <AlertTriangle className="text-yellow-500" size={24} />;
      case 'low': return <CheckCircle className="text-green-500" size={24} />;
      default: return <FileText className="text-gray-400" size={24} />;
    }
  };

  return (
    <div className="tool-page">
      <MatrixBackground />
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => onBack ? onBack() : navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <AlertTriangle size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="Contract Review" />
            <p className="tool-subtitle">Scan contracts for key clauses, risks, and deviations with Lawra's AI assistance</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {!scanResults ? (
          <>
            {/* Upload and Scan Card */}
            <Card className="generation-form-card">
              <CardHeader>
                <CardTitle>Contract Scan & Risk Assessment</CardTitle>
                <CardDescription>
                  Upload a PDF or DOCX contract and get instant risk assessment with key clause identification
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="form-group">
                  <label className="upload-label">Contract File *</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="file-input"
                      accept=".pdf,.docx"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="file-input" className="file-upload-label">
                      <div className="upload-icon-tool">
                        {file ? <FileText size={48} /> : <Upload size={48} />}
                      </div>
                      <p className="upload-text-tool">
                        {file ? file.name : 'Click to upload contract (PDF or DOCX)'}
                      </p>
                      <p className="upload-subtext-tool">Maximum file size: 10MB</p>
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <AnimatedActionButton
                    onClick={handleScan} 
                    disabled={scanning || !file}
                    loading={scanning}
                    size="lg"
                  >
                    Analyse Contract
                  </AnimatedActionButton>
                  <LedProgressLine isActive={scanning} />
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          /* Results View */
          <>
            <div className="results-header">
              <h2>Scan Results</h2>
              <Button onClick={handleReset} variant="outline">
                New Scan
              </Button>
            </div>

            {/* Overall Risk Assessment */}
            <Card className="result-card">
              <CardHeader>
                <div className="risk-summary-header">
                  {getRiskIcon(scanResults.overall_risk_level)}
                  <div>
                    <CardTitle>
                      Overall Risk Level: <span className={getRiskLevelColor(scanResults.overall_risk_level)}>{scanResults.overall_risk_level}</span>
                    </CardTitle>
                    <CardDescription>Comprehensive risk assessment of the contract</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div 
                  className="analysis-content"
                  dangerouslySetInnerHTML={{ __html: scanResults.summary }}
                />
              </CardContent>
            </Card>

            {/* Key Clauses */}
            {scanResults.key_clauses && scanResults.key_clauses.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>Key Clauses Identified</CardTitle>
                  <CardDescription>Critical clauses found in the contract</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="clauses-grid">
                    {scanResults.key_clauses.map((clause, index) => (
                      <div key={index} className="clause-item">
                        <div className="clause-header">
                          {getRiskIcon(clause.risk_level)}
                          <h4>{clause.clause_type}</h4>
                        </div>
                        <div 
                          className="analysis-content"
                          dangerouslySetInnerHTML={{ __html: clause.details }}
                        />
                        {clause.location && (
                          <p className="clause-location">Found in: {clause.location}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Risk Flags */}
            {scanResults.risk_flags && scanResults.risk_flags.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>
                    <AlertTriangle className="inline mr-2" size={24} />
                    Risk Flags
                  </CardTitle>
                  <CardDescription>Identified risks and concerns</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="risks-list">
                    {scanResults.risk_flags.map((risk, index) => (
                      <div key={index} className={`risk-item risk-${risk.severity?.toLowerCase()}`}>
                        <div className="risk-header">
                          {getRiskIcon(risk.severity)}
                          <span className={`risk-severity ${getRiskLevelColor(risk.severity)}`}>
                            {risk.severity} Risk
                          </span>
                        </div>
                        <h4>{risk.issue}</h4>
                        <div 
                          className="analysis-content"
                          dangerouslySetInnerHTML={{ __html: risk.description }}
                        />
                        {risk.recommendation && (
                          <div className="risk-recommendation">
                            <strong>Recommendation:</strong> {risk.recommendation}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Deviations */}
            {scanResults.deviations && scanResults.deviations.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>Deviations from Standard Terms</CardTitle>
                  <CardDescription>Non-standard clauses or unusual terms identified</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="deviations-list">
                    {scanResults.deviations.map((deviation, index) => (
                      <div key={index} className="deviation-item">
                        <div className="deviation-header">
                          <AlertTriangle size={20} className="text-yellow-500" />
                          <h4>{deviation.clause_type}</h4>
                        </div>
                        <div 
                          className="analysis-content"
                          dangerouslySetInnerHTML={{ __html: deviation.description }}
                        />
                        {deviation.standard_expectation && (
                          <div className="standard-expectation">
                            <strong>Standard expectation:</strong> {deviation.standard_expectation}
                          </div>
                        )}
                      </div>
                    ))}
        <ReportIssueBar toolName="Contract Scanner" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommendations */}
            {scanResults.recommendations && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>Recommendations</CardTitle>
                  <CardDescription>Suggested actions and next steps</CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    className="analysis-content"
                    dangerouslySetInnerHTML={{ __html: scanResults.recommendations }}
                  />
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContractReviewScanner;

